import React, { useState, useRef, useEffect } from 'react';
import { Bell, X, CheckCheck, Trash2 } from 'lucide-react';
import { useSmartAlerts } from '../utils/useSmartAlerts';
import { getMatchLevel } from '../utils/matchingScoreEngine';
import { auth } from '../firebase';
import type { MatchAlert } from '../types';

interface NotificationBellProps {
  onOpenMatch: (alert: MatchAlert) => void;
}

interface OfficeMember {
  firebase_uid: string;
  display_name: string | null;
  email: string | null;
}

const scoreColors: Record<string, string> = {
  perfect: 'text-green-400',
  excellent: 'text-green-400',
  strong: 'text-blue-400',
  moderate: 'text-yellow-400',
  weak: 'text-gray-500',
};

const dotColors: Record<string, string> = {
  perfect: 'bg-green-500',
  excellent: 'bg-green-500',
  strong: 'bg-blue-500',
  moderate: 'bg-yellow-500',
  weak: 'bg-gray-600',
};

function relativeTime(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diff = Math.max(0, now - then);
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return 'כעת';
  if (minutes < 60) return `לפני ${minutes} דקות`;
  if (hours < 24) return `לפני ${hours} שעות`;
  if (days < 7) return `לפני ${days} ימים`;
  return new Date(iso).toLocaleDateString('he-IL');
}

const alertTypeLabels: Record<string, string> = {
  buyer_new: 'לקוח חדש',
  buyer_updated: 'עדכון לקוח',
  property_new: 'נכס חדש',
  property_updated: 'עדכון נכס',
};

function useOfficeMembers(): { members: OfficeMember[]; loaded: boolean } {
  const [members, setMembers] = useState<OfficeMember[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const token = await user.getIdToken();
        const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-office`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!cancelled && data.success && data.office?.members) {
          setMembers(data.office.members);
        }
      } catch {
        // no office — that's fine
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { members, loaded };
}

const NEW_THRESHOLD_MS = 10 * 60 * 1000; // 10 minutes

function getAlertVisualState(alert: MatchAlert): 'new' | 'unread' | 'read' {
  if (alert.status === 'viewed') return 'read';
  const ageMs = Date.now() - new Date(alert.created_at).getTime();
  return ageMs < NEW_THRESHOLD_MS ? 'new' : 'unread';
}

const NotificationBell: React.FC<NotificationBellProps> = ({ onOpenMatch }) => {
  const { alerts, unreadCount, loading, refresh, markViewed, markAllViewed, clearAlerts } = useSmartAlerts();
  const { members } = useOfficeMembers();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        bellRef.current && !bellRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) refresh();
  };

  const handleAlertClick = async (alert: MatchAlert) => {
    await markViewed(alert.id);
    onOpenMatch(alert);
    setOpen(false);
  };

  const currentUid = auth.currentUser?.uid;
  const getOwnerName = (alert: MatchAlert): string | null => {
    if (!alert.property_agent_id || alert.property_agent_id === currentUid) return null;
    const member = members.find(m => m.firebase_uid === alert.property_agent_id);
    return member?.display_name || null;
  };

  return (
    <div className="relative shrink-0">
      <button
        ref={bellRef}
        onClick={handleOpen}
        className="relative flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-transparent hover:bg-gray-800 border border-transparent hover:border-gray-700 text-gray-400 hover:text-gray-200 rounded-md transition-all"
        aria-label="Smart Alerts"
      >
        <Bell size={14} />
        {unreadCount > 0 && (
          <span className="absolute -top-1.5 -left-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-blue-500 rounded-full ring-2 ring-gray-900">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={dropdownRef}
          className="absolute left-0 top-full mt-2 w-80 bg-gray-800 border border-gray-700 rounded-lg shadow-2xl z-50 overflow-hidden"
          dir="rtl"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 bg-gray-750">
            <span className="text-sm font-semibold text-white">Smart Alerts</span>
            <button
              onClick={() => setOpen(false)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X size={16} />
            </button>
          </div>

          {/* Body */}
          <div className="max-h-96 overflow-y-auto">
            {loading && alerts.length === 0 ? (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-500" />
              </div>
            ) : alerts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                <Bell size={32} className="text-gray-600 mb-3" />
                <p className="text-sm text-gray-400">אין התראות חדשות</p>
                <p className="text-xs text-gray-600 mt-1">No new Smart Alerts</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-700/50">
                {alerts.map(alert => {
                  const level = getMatchLevel(alert.match_score);
                  const ownerName = getOwnerName(alert);
                  const visualState = getAlertVisualState(alert);
                  return (
                    <button
                      key={alert.id}
                      onClick={() => handleAlertClick(alert)}
                      className={`w-full text-right px-4 py-3 hover:bg-gray-700/50 transition-colors block ${
                        visualState === 'read' ? 'opacity-60' : ''
                      }`}
                    >
                      {/* Score row */}
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className={`w-2 h-2 rounded-full ${dotColors[level] || 'bg-gray-600'}`} />
                        <span className={`text-lg font-bold ${scoreColors[level] || 'text-gray-400'}`}>
                          {alert.match_score}%
                        </span>
                        {/* Read-state badge */}
                        {visualState === 'new' && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-blue-500/20 border border-blue-500/40 text-[9px] font-bold text-blue-400 leading-none">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                            חדש
                          </span>
                        )}
                        {visualState === 'unread' && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gray-600/30 border border-gray-500/40 text-[9px] font-medium text-gray-300 leading-none">
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                            לא נקראה
                          </span>
                        )}
                        {visualState === 'read' && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gray-700/40 border border-gray-700/40 text-[9px] font-medium text-gray-500 leading-none">
                            <CheckCheck size={9} />
                            נקראה
                          </span>
                        )}
                        <span className="text-[10px] text-gray-500 mr-auto">
                          {alertTypeLabels[alert.alert_type] || alert.alert_type}
                        </span>
                      </div>

                      {/* Buyer name */}
                      {alert.buyer_name && (
                        <p className="text-sm font-medium text-white truncate">
                          {alert.buyer_name}
                        </p>
                      )}

                      {/* Property address */}
                      {alert.property_address && (
                        <p className="text-xs text-gray-400 truncate mt-0.5">
                          {alert.property_address}
                        </p>
                      )}

                      {/* Property owner — only if another agent */}
                      {ownerName && (
                        <p className="text-[11px] text-amber-400/80 mt-1">
                          נכס של: {ownerName}
                        </p>
                      )}

                      {/* Relative time */}
                      <p className="text-[10px] text-gray-600 mt-1">
                        {relativeTime(alert.created_at)}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {alerts.length > 0 && (
            <div className="border-t border-gray-700 bg-gray-750">
              <div className="flex items-center justify-between px-4 py-2 gap-2">
                <button
                  onClick={() => markAllViewed()}
                  disabled={unreadCount === 0}
                  className="flex items-center gap-1.5 text-[11px] font-medium text-gray-400 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <CheckCheck size={13} />
                  סמן הכל כנקרא
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('להסיר את ההתראות הפעילות מהרשימה?')) {
                      clearAlerts();
                    }
                  }}
                  className="flex items-center gap-1.5 text-[11px] font-medium text-gray-400 hover:text-red-400 transition-colors"
                >
                  <Trash2 size={13} />
                  נקה התראות
                </button>
              </div>
              <p className="text-[10px] text-gray-500 text-center pb-2">
                {unreadCount} התראות שלא נקראו
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
